import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ApiCallService from "../../../Service/ApiCallService";
import VibrancyStatusChip from "./VibrancyStatusChip";
import VibrancySubmissionForm from "./VibrancySubmissionForm";

const getLocationName = (chapter, key) => {
  const value = chapter?.location?.[key];
  return value?.[`${key}Name`] || value?.name || "";
};

const ProspectiveChapterList = () => {
  const [chapters, setChapters] = useState([]);
  const [filters, setFilters] = useState({ search: "", district: "", state: "", region: "" });
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadChapters = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/chapters/prospective`,
        filters,
      );
      setChapters(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to load prospective chapters:", err);
      setError("Failed to load prospective chapters.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  const filterOptions = useMemo(() => {
    const build = (key) => {
      const map = new Map();
      chapters.forEach((chapter) => {
        const value = chapter?.location?.[key];
        if (value?._id) map.set(value._id, getLocationName(chapter, key));
      });
      return Array.from(map, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
    };
    return {
      district: build("district"),
      state: build("state"),
      region: build("region"),
    };
  }, [chapters]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitSuccess = () => {
    loadChapters();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f7f5fb", minHeight: "100vh" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={900} color="#3f2367">
            Program Chapter Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Initiate Foundation Program submissions for prospective chapters.
          </Typography>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              name="search"
              label="Search chapter"
              value={filters.search}
              onChange={handleFilterChange}
              fullWidth
            />
            {["district", "state", "region"].map((key) => (
              <TextField
                key={key}
                name={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={filters[key]}
                onChange={handleFilterChange}
                select
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions[key].map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            <Button variant="contained" onClick={loadChapters} sx={{ minWidth: 120 }}>
              Search
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: "#efe8f8" }}>
              <TableRow>
                <TableCell>Chapter</TableCell>
                <TableCell>District</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : chapters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No prospective chapters found.
                  </TableCell>
                </TableRow>
              ) : (
                chapters.map((chapter) => (
                  <TableRow key={chapter._id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{chapter.chapterName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {chapter.chapterUniqueCode || "No code"}
                      </Typography>
                    </TableCell>
                    <TableCell>{getLocationName(chapter, "district") || "N/A"}</TableCell>
                    <TableCell>{getLocationName(chapter, "state") || "N/A"}</TableCell>
                    <TableCell>
                      <VibrancyStatusChip status={chapter.chapterLifecycleStatus || "Prospective"} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        startIcon={<RocketLaunchIcon />}
                        onClick={() => setSelectedChapter(chapter)}
                      >
                        Submit 
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <VibrancySubmissionForm
        open={Boolean(selectedChapter)}
        chapter={selectedChapter}
        onClose={() => setSelectedChapter(null)}
        onSubmitted={handleSubmitSuccess}
      />
    </Box>
  );
};

export default ProspectiveChapterList;
